import { Component, OnInit, ViewChild } from '@angular/core';
import { DeviceService } from '../home/site-dashboard/device/device.service';
import { Router } from '@angular/router';
import { UsersService } from '../users/users.service';
import { TranslateService } from '@ngx-translate/core';
import { ColumnsConfig, TableConfig, TableData} from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import * as moment from 'moment';
import { CommonService, TimeSource } from '../shared/services/common.service';
import { SCTCustomTable } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/sct-table.component';
import { SideMenuService } from '../shared/side-menu/side-menu.service';

@Component({
	selector: 'app-devices-connectivity',
	templateUrl: './devices-connectivity.component.html',
	styleUrls: ['./devices-connectivity.component.css']
})
export class DevicesConnectivityComponent implements OnInit {
	@ViewChild("sctCustomTable", { static: true }) sctCustomTable!: SCTCustomTable;

	constructor(
		private router: Router,
		private usersService: UsersService,
		private devicesService: DeviceService,
		private translateService: TranslateService,
		private commonService: CommonService,
		private sideMenuService: SideMenuService,
	) { }

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: false,
		hasPagination: true,
		pageSize: 20,
		isBackendPagination: true,
		fitScreen: true
	};

	data: any;
	filter: any = {};
	sort: any = {};
	pageIndex: number;

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		last_connect_time: TimeSource.PcTime,
		// Both built with moment.unix(...).format('MM/DD/yyyy') - no .utc(), so browser zone.
		chargLink_installation_date: TimeSource.PcTime,
		site_installation_date: TimeSource.PcTime,
	};

	columnConfig: ColumnsConfig[] = [
		{ key: 'mac_address', name: this.translateService.instant('g.mac_address'), type: "link"},
		{ key: 'customer_name', name: this.translateService.instant('g.customer_name'), type: "link"},
		{ key: 'site_name', name: this.translateService.instant('g.site_name'), type: "link"},
		{ key: 'serial_number', name: this.translateService.instant('g.serial_number'), type: 'string'},
		{ key: 'truck_sn', name: this.translateService.instant('devices.device_sn'), type: 'string'},
		{ key: 'charger_manufacturer', name: this.translateService.instant('bulk_fill_device_info.truck_manufacturer'), type: 'string'},
		{ key: 'charger_model', name: this.translateService.instant('bulk_fill_device_info.truck_type'), type: 'string'},
		{ key: 'chargLink_installation_date', name: this.translateService.instant('devices.device_installation_date'), type: 'date'},
		{ key: 'site_installation_date', name: this.translateService.instant('site.site_installation_date'), type: 'date'},
		{ key: 'last_connect_time', name: this.translateService.instant('devices.last_connect_time'), type: 'date_with_ref'},
		{ key: 'last_connect_type', name: this.translateService.instant('devices.last_connect_type'), type: 'string',  filterType: 'dropdown', items: this.devicesService.lastConnectionType },
		{ key: 'installer_name', name: this.translateService.instant('site.installer_name'), type: 'string'},
		{ key: 'sales_person_name', name: this.translateService.instant('site.sales_person_name'), type: 'string'},
		{ key: 'site_state', name: this.translateService.instant('site.site_state'), type: 'string'},
		{ key: 'is_sr', name: this.translateService.instant('smart_rebates_reports.is_sr'), type: 'string', filterType: 'dropdown', items: this.devicesService.isSmartRebate},
		{ key: 'country', name: this.translateService.instant('site_edit.country'), type: 'string'},
		{ key: 'hw_version', name: this.translateService.instant('devices.hw_version'), type: 'string'},
		{ key: 'fw_version', name: this.translateService.instant('g.fw_version'), type: 'string'},
	];

	ngOnInit() {
		this.commonService.applyTimeSourceTooltips(this.columnConfig, this.timeSourceByColumn);

		this.sideMenuService.hide();

		if (!this.usersService.hasAccessPermission(null, 'noc'))
			this.router.navigate(['/unauthorized']);
		return this.getDevicesConnectivityData({currentPage: 1});
	}

	getDevicesConnectivityData(paginationData) {
		if (!this.sctCustomTable)
			return;

		if (paginationData.currentPage == 1)
			this.sctCustomTable.backendPaginationInit();

		this.pageIndex = paginationData.currentPage;

		const options: any = {
			currentPage: paginationData.currentPage,
			limit: this.sctCustomTable.config.pageSize,
			sort: JSON.stringify(paginationData.sort || {}),
			filters: JSON.stringify(paginationData.filters || {}),
		};

		this.filter = options.filters;
		this.sort = options.sort;

		this.devicesService.getDevicesConnectivityData(options).subscribe((response: any) => {
			this.data = (response.data || []).map(item => this.commonService.decompress(item, 'getDevicesConnectivityData'));
			const tableData =[];
				this.data.forEach((item: any) => {
					tableData.push({
						mac_address: {value: item.mac_address, link: ['/', item.customer_id, item.site_id, item.mac_address, 'settings']},
						customer_name: {value: item.customer_name, link: ['/', item.customer_id]},
						site_name: {value: item.site_name, link: ['/', item.customer_id, item.site_id]},
						serial_number: { value: item.serial_number },
						truck_sn: { value: item.truck_sn },
						charger_manufacturer: { value: item.truck_manufacturer },
						charger_model: { value: item.truck_type },
						chargLink_installation_date: { value: item.device_installation_date ?  moment.unix((new Date(item.device_installation_date).getTime() || 0) / 1000).format('MM/DD/yyyy') : null },
						site_installation_date: { value: item.site_installation_date ?  moment.unix((new Date(item.site_installation_date).getTime() || 0) / 1000).format('MM/DD/yyyy') : null },
						last_connect_time: { value: item.last_connect_time ? moment.unix((new Date(item.last_connect_time).getTime() || 0) / 1000).format('MM/DD/yyyy hh:mm:ss a') : null },
						last_connect_type: {value: item.last_connection_type },
						installer_name: {value: item.electrician_contact_name},
						sales_person_name: { value: item.sales_person_name} ,
						site_state: { value: item.state_id },
						is_sr: { value: item.is_smart_rebates == true ? 'Yes' : 'No' },
						country: { value: item.country_id },
						hw_version: { value: item.hw_version },
						fw_version: { value: item.fw_version },
					})
				});
				this.tableData = tableData;
				this.sctCustomTable.updatePagination(response.totalDataCount);
		})
	}

	exportToCsvFile() {
		const options: any = {
			limit: this.sctCustomTable.config.pageSize,
			currentPage: this.pageIndex,
			columns: this.getTableColumn(),
		};

		if (this.filter)
			options.filters = this.filter;

		if (this.sort)
			options.sort = this.sort;

		this.devicesService.exportDevicesConnectivityCSV(options).subscribe((data: any) => {
			const blob = new Blob([data], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;

			let fileName = this.translateService.instant('devices.device_connectivity') + '.csv';

			a.download = fileName;
			a.click();

			window.URL.revokeObjectURL(url);
		});
	}

	getTableColumn() {
		let columns = [];
		if (this.sctCustomTable)
			columns = this.sctCustomTable.shownColumnsConfig.map((column) => column.key);

		return columns;
	}
}
